import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Divider,
  FormHelperText,
  Stack,
  TextField,
  ToggleButton,
  Tooltip,
  Typography
} from '@mui/material';
import {
  FormatBold,
  FormatListBulleted,
  FormatQuote,
  Link as LinkIcon,
  LinkOff
} from '@mui/icons-material';

import { isSafeHref, normalizeContentHtml } from './richText';

interface RichTextFieldProps {
  label: string;
  value: string;
  // eslint-disable-next-line no-unused-vars -- parameter name documents the typed callback
  onChange: (html: string) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  minHeight?: number;
}

/**
 * A deliberately small editor: paragraph, bullet list, bold and link. Whatever
 * the browser produces is passed through `normalizeContentHtml` before it leaves
 * this component, so the value handed upward is always in the same `<p>` /
 * `<ul><li>` vocabulary the stored config uses.
 */
const RichTextField: React.FC<RichTextFieldProps> = ({
  label,
  value,
  onChange,
  error,
  helperText,
  disabled,
  minHeight = 140
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastEmitted = useRef<string>('');
  const savedRange = useRef<Range | null>(null);

  const [focused, setFocused] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkError, setLinkError] = useState('');

  // Only write into the DOM when the value changed somewhere other than here,
  // otherwise every keystroke would reset the caret to the start.
  useEffect(() => {
    const element = editorRef.current;
    if (!element) {
      return;
    }
    if (value !== lastEmitted.current) {
      element.innerHTML = value ?? '';
      lastEmitted.current = value ?? '';
    }
  }, [value]);

  const emit = useCallback(() => {
    const element = editorRef.current;
    if (!element) {
      return;
    }
    const html = normalizeContentHtml(element.innerHTML);
    lastEmitted.current = html;
    onChange(html);
  }, [onChange]);

  const runCommand = useCallback(
    (command: string, argument?: string) => {
      if (disabled) {
        return;
      }
      editorRef.current?.focus();
      document.execCommand(command, false, argument);
      emit();
    },
    [disabled, emit]
  );

  const handleFocus = useCallback(() => {
    setFocused(true);
    // Makes Enter produce <p> rather than <div>, which keeps the output closer
    // to the stored shape before normalisation even runs.
    try {
      document.execCommand('defaultParagraphSeparator', false, 'p');
    } catch (err) {
      // Not supported everywhere; normalisation covers the difference.
    }
  }, []);

  /** The anchor the current selection sits inside, if any. */
  const findSelectedAnchor = (): HTMLAnchorElement | null => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return null;
    }
    let node: Node | null = selection.getRangeAt(0).commonAncestorContainer;
    while (node && node !== editorRef.current) {
      if (node.nodeType === 1 && (node as Element).tagName === 'A') {
        return node as HTMLAnchorElement;
      }
      node = node.parentNode;
    }
    return null;
  };

  const closeLinkEditor = useCallback(() => {
    setLinkOpen(false);
    setLinkError('');
    setLinkUrl('');
    savedRange.current = null;
  }, []);

  /**
   * Focusing the URL field drops the editor's selection, so the range is cloned
   * here (while the toolbar's preventDefault still has it intact) and restored
   * when the link is applied.
   */
  const openLinkEditor = useCallback(() => {
    if (disabled) {
      return;
    }
    const selection = window.getSelection();
    const anchor = findSelectedAnchor();

    if (!selection || selection.rangeCount === 0 || (selection.isCollapsed && !anchor)) {
      setLinkUrl('');
      setLinkError('Select the text you want to link first.');
      setLinkOpen(true);
      return;
    }

    savedRange.current = selection.getRangeAt(0).cloneRange();
    setLinkUrl(anchor?.getAttribute('href') ?? 'https://');
    setLinkError('');
    setLinkOpen(true);
  }, [disabled]);

  const applyLink = useCallback(() => {
    const href = linkUrl.trim();

    if (!savedRange.current) {
      setLinkError('Select the text you want to link first.');
      return;
    }
    if (!isSafeHref(href)) {
      setLinkError('Enter an http, https or mailto URL.');
      return;
    }

    editorRef.current?.focus();
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(savedRange.current);

    document.execCommand('createLink', false, href);
    emit();
    closeLinkEditor();
  }, [linkUrl, emit, closeLinkEditor]);

  const resolveBorderColor = () => {
    if (error) {
      return 'error.main';
    }
    return focused ? 'primary.main' : 'rgba(0, 0, 0, 0.23)';
  };
  const borderColor = resolveBorderColor();

  const toolbarButton = (
    key: string,
    title: string,
    icon: React.ReactNode,
    onClick: () => void
  ) => (
    <Tooltip title={title} key={key}>
      <span>
        <ToggleButton
          value={key}
          size='small'
          disabled={disabled}
          onMouseDown={event => event.preventDefault()}
          onClick={onClick}>
          {icon}
        </ToggleButton>
      </span>
    </Tooltip>
  );

  return (
    <Box>
      <Typography
        variant='caption'
        sx={{ color: error ? 'error.main' : 'text.secondary', display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Box
        sx={{
          border: 1,
          borderColor,
          borderRadius: 1,
          boxShadow:
            focused && !error ? theme => `0 0 0 1px ${theme.palette.primary.main}` : 'none',
          bgcolor: disabled ? 'action.disabledBackground' : 'transparent'
        }}>
        <Stack direction='row' spacing={0.5} sx={{ p: 0.5 }}>
          {toolbarButton('bold', 'Bold', <FormatBold fontSize='small' />, () => runCommand('bold'))}
          {toolbarButton('ul', 'Bullet list', <FormatListBulleted fontSize='small' />, () =>
            runCommand('insertUnorderedList')
          )}
          {toolbarButton('p', 'Paragraph', <FormatQuote fontSize='small' />, () =>
            runCommand('formatBlock', 'p')
          )}
          {toolbarButton('link', 'Add link', <LinkIcon fontSize='small' />, openLinkEditor)}
          {toolbarButton('unlink', 'Remove link', <LinkOff fontSize='small' />, () =>
            runCommand('unlink')
          )}
        </Stack>

        <Collapse in={linkOpen} unmountOnExit>
          <Divider />
          <Stack direction='row' spacing={1} alignItems='flex-start' sx={{ p: 1 }}>
            <TextField
              autoFocus
              size='small'
              fullWidth
              placeholder='https://'
              label='Link URL'
              value={linkUrl}
              error={Boolean(linkError)}
              helperText={linkError || ' '}
              onChange={event => {
                setLinkUrl(event.target.value);
                setLinkError('');
              }}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  applyLink();
                }
                if (event.key === 'Escape') {
                  event.preventDefault();
                  closeLinkEditor();
                }
              }}
            />
            <Button variant='contained' size='small' sx={{ mt: 0.5 }} onClick={applyLink}>
              Apply
            </Button>
            <Button size='small' sx={{ mt: 0.5 }} onClick={closeLinkEditor}>
              Cancel
            </Button>
          </Stack>
        </Collapse>

        <Divider />
        <Box
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={emit}
          onBlur={() => {
            setFocused(false);
            emit();
          }}
          onFocus={handleFocus}
          sx={{
            minHeight,
            p: 1.5,
            outline: 'none',
            fontSize: 14,
            lineHeight: 1.6,
            overflowY: 'auto',
            '& p': { m: 0, mb: 1 },
            '& p:last-child': { mb: 0 },
            '& ul': { my: 0, pl: 3 },
            '& li': { mb: 0.5 }
          }}
        />
      </Box>
      {helperText ? <FormHelperText error={error}>{helperText}</FormHelperText> : null}
    </Box>
  );
};

RichTextField.defaultProps = {
  error: false,
  helperText: undefined,
  disabled: false,
  minHeight: 140
};

export default RichTextField;
